import type { ItemNode, OrdersParseTree } from "../parser/types";
import { decideBlankType } from "./typing-rules";
import type { BlankField } from "./types";

const BLANK = /_9C|_+/g;

interface LocatedItem {
  readonly groupId: string;
  readonly item: ItemNode;
}

function itemsInDeliveryOrder(tree: OrdersParseTree): readonly LocatedItem[] {
  return tree.paragraphs.flatMap((paragraph) => [
    ...paragraph.items.map((item) => ({ groupId: paragraph.id, item })),
    ...paragraph.subparagraphs.flatMap((subparagraph) =>
      subparagraph.items.map((item) => ({ groupId: paragraph.id, item })),
    ),
  ]);
}

/** How much of the line to keep on each side of the blank. */
const CONTEXT_BEFORE = 44;
const CONTEXT_AFTER = 24;

/** How the blank being filled is marked inside its own line. */
const HERE = "[ ]";

/** Marks a line that was cut to fit the window, so a cut reads as a cut. */
const ELLIPSIS = "\u2026";

/**
 * The blank's own line, trimmed to a window and with this blank marked.
 *
 * A row has to tell the trainee which blank on the card it is. Naming the
 * field type and a counter -- "control measure 3" -- tells him nothing, and
 * forty rows of it read as forty blank rows. Quoting the words around the
 * blank is not enough either: two blanks on one line can share the same
 * neighbouring words and then two rows read identically. So the line is
 * quoted with this blank, and only this one, marked.
 *
 * Other blanks on the line keep their underscores, because that is how they
 * appear on the paper card in front of him. Markdown emphasis is dropped,
 * since the label is read by a person and not parsed.
 */
function blankContext(text: string, start: number, end: number): string {
  const tidy = (part: string): string =>
    part.replaceAll(/\*{1,2}/g, "").replace(/\s+/g, " ");
  const head = tidy(text.slice(Math.max(0, start - CONTEXT_BEFORE), start));
  const tail = tidy(text.slice(end, end + CONTEXT_AFTER));

  // Finding D of 7 October 2026: the quoted line cut mid-sentence and said
  // nothing about it, so "Route taken from" read as the end of the line.
  // Cut at a word boundary, and mark the cut with an ellipsis so the
  // trainee can see that the line goes on.
  const trimmedStart = start > CONTEXT_BEFORE;
  const headWords = head.trimStart();
  const from =
    trimmedStart && headWords.includes(" ")
      ? `${ELLIPSIS}${headWords.slice(headWords.indexOf(" "))}`
      : headWords;

  const trimmedEnd = end + CONTEXT_AFTER < text.length;
  const tailWords = tail.trimEnd();
  const cut = tailWords.lastIndexOf(" ");
  const to =
    trimmedEnd && cut > 0
      ? `${tailWords.slice(0, cut)}${ELLIPSIS}`
      : trimmedEnd
        ? `${tailWords}${ELLIPSIS}`
        : tailWords;

  return `${from.trimEnd()} ${HERE} ${to.trimStart()}`
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * The kind of a blank and how it was decided.
 *
 * The rules read the text immediately before and after this blank, not the
 * whole line, because a line can hold several blanks of different kinds.
 * Ruling 52 of 7 October 2026.
 */
function typed(
  item: ItemNode,
  token: string,
  at: number,
): Pick<BlankField, "fieldType" | "typing"> {
  const decision = decideBlankType({
    after: item.displayText.slice(at + token.length),
    before: item.displayText.slice(0, at),
    item,
    token,
    width: token === "_9C" ? 1 : token.length,
  });
  return {
    fieldType: decision.type,
    typing: { reason: decision.reason, rule: decision.rule },
  };
}

function fieldsForItem(item: ItemNode, groupId: string): readonly BlankField[] {
  const fields: BlankField[] = [];
  for (const match of item.displayText.matchAll(BLANK)) {
    const sourceToken = match[0];
    const index = fields.length + 1;
    fields.push({
      id: `${item.id}/blank-${String(index).padStart(2, "0")}`,
      nodeId: item.id,
      groupId,
      sourceToken,
      length: sourceToken === "_9C" ? 1 : sourceToken.length,
      context: blankContext(
        item.displayText,
        match.index,
        match.index + sourceToken.length,
      ),
      sourceLine: item.sourceLine,
      ...typed(item, sourceToken, match.index),
    });
  }

  if (item.type === "table" && /\|\s*$/.test(item.displayText)) {
    const index = fields.length + 1;
    fields.push({
      id: `${item.id}/blank-${String(index).padStart(2, "0")}`,
      nodeId: item.id,
      groupId,
      // The trailing empty cell of a timings row. Its context is the row,
      // which is what names the timing the trainee has to supply.
      context: blankContext(
        item.displayText,
        item.displayText.length,
        item.displayText.length,
      ),
      sourceToken: "",
      length: 0,
      sourceLine: item.sourceLine,
      fieldType: "time",
      typing: {
        // Not rule 12. Rule 12 — `table-row-cell` — types a blank *inside*
        // a row, where an underscore run marks it. This is the empty final
        // cell of a timings row, which has no token to match and so never
        // reaches the rule table at all. Two different things, and the old
        // name `table-row-trailing-cell` read like a variant of the rule.
        reason:
          "the empty final cell of a timings row is a time; it carries no blank token, so no typing rule sees it",
        rule: "timings-table-empty-final-cell",
      },
    });
  }
  return fields;
}

export function extractBlanks(tree: OrdersParseTree): readonly BlankField[] {
  return itemsInDeliveryOrder(tree).flatMap(({ groupId, item }) =>
    fieldsForItem(item, groupId),
  );
}
